
import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { queryLegalWorkLogs, createLegalWorkLog, updateLegalWorkLog } from "../services/legalWorkLogService";
import type { LegalWorkLog } from "../types/legalWorkLog";
import { exportToExcel } from "../export/excel";
import { Modal } from "../components/Modal";
import { FileUploadKit } from "../components/common/FileUploadKit";
import { useToast } from "../components/Toast";
import { uploadWorkLogFiles } from "../services/storageUpload";
import { getCachedPreferences } from "../services/userPreferences";
import { DateField } from "../components/form/DateField";
import { formatDate, parseDate } from "../utils/dateUtils";

export function LegalWorkLogView() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [logs, setLogs] = useState<LegalWorkLog[]>([]);
  const [loading, setLoading] = useState(false);

  const [startDate, setStartDate] = useState(() => {
    const userId = user?.id || "";
    const prefs = getCachedPreferences(userId);
    const range = prefs?.defaultDateRangeDays || 30;
    const d = new Date();
    d.setDate(d.getDate() - range);
    return formatDate(d);
  });
  const [endDate, setEndDate] = useState(() => formatDate(new Date()));
  const [moduleFilter, setModuleFilter] = useState("ALL");
  const [search, setSearch] = useState("");

  // Viewing detail modal (Read Only)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewingLog, setViewingLog] = useState<LegalWorkLog | null>(null);

  const loadData = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await queryLegalWorkLogs("", startDate, endDate, moduleFilter);
      setLogs(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [startDate, endDate, moduleFilter]);

  const filteredLogs = logs.filter(log => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (log.title || "").toLowerCase().includes(s) ||
      (log.refNo || "").toLowerCase().includes(s) ||
      (log.summary || "").toLowerCase().includes(s)
    );
  });

  const handleExport = () => {
    if (filteredLogs.length === 0) {
      alert("無資料可匯出");
      return;
    }
    const exportData = filteredLogs.map(log => ({
      時間: log.occurredAt?.toDate ? log.occurredAt.toDate().toLocaleString() : "",
      模組: log.moduleLabel,
      動作: log.actionLabel,
      標題: log.title,
      編號: log.refNo || "",
      對象部門: log.counterpartOrDept || "",
      摘要: log.summary || "",
      當下狀態: log.finalStatus
    }));
    exportToExcel(`LegalWorkLog_${startDate}_${endDate}`, exportData);
  };

  return (
    <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
      <div className="section" style={{ marginBottom: 16, padding: "12px 16px", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12 }}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{display:"flex", flexDirection:"column", gap:4}}>
             <label style={{fontSize:11, color:"var(--muted)"}}>日期範圍</label>
             <div style={{display:"flex", gap:6}}>
               <div style={{width: 140}}>
                 <DateField value={parseDate(startDate)} onChange={d => setStartDate(formatDate(d))} />
               </div>
               <span style={{alignSelf:"center", color: "var(--muted)"}}>~</span>
               <div style={{width: 140}}>
                 <DateField value={parseDate(endDate)} onChange={d => setEndDate(formatDate(d))} />
               </div>
             </div>
          </div>
          <div style={{display:"flex", flexDirection:"column", gap:4}}>
             <label style={{fontSize:11, color:"var(--muted)"}}>模組</label>
             <select className="input" style={{ width: 180 }} value={moduleFilter} onChange={e => setModuleFilter(e.target.value)}>
                <option value="ALL">全部模組</option>
                <option value="CONTRACT_SYSTEM">合約系統管理</option>
                <option value="CONTRACT_DRAFT">合約草案審查</option>
                <option value="DISPUTE">案件及爭議管理</option>
                <option value="IP_PROTECTION">智財申請與維權</option>
                <option value="STAMP">用印申請與紀錄</option>
                <option value="PUBLIC_DOC">公文函覆</option>
                <option value="GOVERNANCE">公司治理/法遵</option>
                <option value="TEMPLATE_CENTER">範本中心</option>
                <option value="BRAND">品牌資產管理</option>
                <option value="OTHER">其他</option>
             </select>
          </div>
          <div style={{display:"flex", flexDirection:"column", gap:4, flex: 1, minWidth: 200}}>
             <label style={{fontSize:11, color:"var(--muted)"}}>關鍵字搜尋</label>
             <input className="input" placeholder="搜尋標題、編號、摘要..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div style={{ alignSelf: "flex-end", display: "flex", gap: 8 }}>
            <button className="smallbtn" style={{ height: 42, background: "#fff", color: "#000", border: "none", fontWeight: "bold" }} onClick={handleExport}>匯出 CSV</button>
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflowX: "auto", width: "100%", maxWidth: "100%", border: "1px solid var(--border)", borderRadius: 12, flex: 1 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 800 }}>
          <thead style={{ background: "#222", textAlign: "left", color: "var(--muted)" }}>
            <tr>
              <th style={{ padding: "12px 16px", whiteSpace: "nowrap" }}>時間</th>
              <th style={{ padding: "12px 16px", whiteSpace: "nowrap" }}>模組 / 動作</th>
              <th style={{ padding: "12px 16px" }}>標題 / 編號</th>
              <th style={{ padding: "12px 16px" }}>對象 / 部門</th>
              <th style={{ padding: "12px 16px" }}>摘要說明</th>
              <th style={{ padding: "12px 16px", whiteSpace: "nowrap" }}>狀態</th>
              <th style={{ padding: "12px 16px", whiteSpace: "nowrap", textAlign: "right" }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} style={{padding:24, textAlign:"center", color: "var(--muted)"}}>資料載入中...</td></tr>}
            {!loading && filteredLogs.length === 0 && (
              <tr><td colSpan={7} style={{padding:24, textAlign:"center", color:"var(--muted)"}}>此條件下無紀錄</td></tr>
            )}
            {!loading && filteredLogs.map(log => (
              <tr key={log.id} style={{ borderBottom: "1px solid #333" }}>
                <td style={{ padding: "12px 16px", whiteSpace: "nowrap", color: "var(--muted)", verticalAlign: "top" }}>
                  {log.occurredAt?.toDate ? log.occurredAt.toDate().toLocaleString([], {year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'}) : "-"}
                </td>
                <td style={{ padding: "12px 16px", verticalAlign: "top" }}>
                  <div style={{fontWeight: 500}}>{log.moduleLabel}</div>
                  <span className="badge" style={{ marginTop: 4, display: "inline-block", background: "rgba(255,255,255,0.1)", border: "none", color: "var(--muted)" }}>{log.actionLabel}</span>
                </td>
                <td style={{ padding: "12px 16px", verticalAlign: "top" }}>
                  <div style={{ fontWeight: 600, color: "#fff", marginBottom: 2 }}>{log.title}</div>
                  {log.refNo && <div style={{ fontSize: 12, color: "#aaa", fontFamily: "monospace" }}>{log.refNo}</div>}
                </td>
                <td style={{ padding: "12px 16px", verticalAlign: "top" }}>{log.counterpartOrDept || "-"}</td>
                <td style={{ padding: "12px 16px", maxWidth: 300, color: "#ccc", verticalAlign: "top" }}>
                  <div style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", fontSize: 12, lineHeight: 1.5 }} title={log.summary}>
                    {log.summary || "-"}
                  </div>
                  {log.attachments && log.attachments.length > 0 && <div style={{fontSize:11, color:"#60a5fa", marginTop:4}}>📎 {log.attachments.length} 個附件</div>}
                </td>
                <td style={{ padding: "12px 16px", verticalAlign: "top" }}>
                  <span className="badge" style={{ color: ["APPROVED", "CASE_CLOSED", "ACTIVE", "已處理完畢", "已核准"].includes(log.finalStatus) ? "var(--ok)" : "#fbbf24", borderColor: "currentColor" }}>
                    {log.finalStatus}
                  </span>
                </td>
                <td style={{ padding: "12px 16px", textAlign: "right", verticalAlign: "top" }}>
                  <div style={{display:'flex', flexDirection:'column', gap:4, alignItems:'flex-end'}}>
                    <button className="smallbtn" style={{ fontSize: 12, padding: "4px 8px" }} onClick={() => { setViewingLog(log); setIsModalOpen(true); }}>檢視</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isModalOpen && viewingLog && (
        <WorkLogDetailModal 
            data={viewingLog} 
            onClose={() => setIsModalOpen(false)} 
        />
      )}
    </div>
  );
}

function WorkLogDetailModal({ data, onClose }: { data: LegalWorkLog, onClose: () => void }) {
    return (
        <Modal title="工作日誌詳情" onClose={onClose} width={600}>
            <div className="grid">
                <div className="field"><label className="label">模組</label><div className="input" style={{background: 'transparent', border:'none'}}>{data.moduleLabel}</div></div>
                <div className="field"><label className="label">標題</label><div className="input" style={{background: 'transparent', border:'none'}}>{data.title}</div></div>
                <div className="field"><label className="label">動作</label><div className="input" style={{background: 'transparent', border:'none'}}>{data.actionLabel}</div></div>
                <div className="field"><label className="label">狀態</label><div className="input" style={{background: 'transparent', border:'none'}}>{data.finalStatus}</div></div>
                <div className="field" style={{gridColumn:"1/-1"}}><label className="label">摘要</label>
                    <div style={{background:'rgba(255,255,255,0.05)', padding:10, borderRadius:6, minHeight:60, whiteSpace:'pre-wrap'}}>{data.summary || "(無)"}</div>
                </div>
            </div>
            
            {data.attachments && data.attachments.length > 0 && (
                <div style={{marginTop:20}}>
                    <div className="label" style={{marginBottom:8}}>附件</div>
                    <FileUploadKit existingFiles={data.attachments} disabled />
                </div>
            )}

            <div className="row" style={{ marginTop: 20, justifyContent: "flex-end" }}>
                <button className="btn" style={{ width: "auto" }} onClick={onClose}>關閉</button>
            </div>
        </Modal>
    );
}
